import { AppointmentDTO } from '@/models/AppointmentDTO';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ActionTypes, GetterTypes } from '@/store-vuex/types';
import InputText from '../inputs/InputText/InputText';
import { CodeDTO } from '@/models/CodeDTO';
import { CodeTranslationDTO } from '@/models/CodeTranslationDTO';
import CodeService from '@/services/code.service';
import InputSelect from '../inputs/InputSelect/InputSelect';
import CheckboxList from '../inputs/CheckboxList/CheckboxList';
import { AppointmentDetailsDTO } from '@/models/AppointmentDetailsDTO';
import { SelectOptionDTO } from '@/models/SelectOptionDTO';
import InformationBlock from '../InformationBlock/InformationBlock';
import EavService from '@/services/eav.services';
import { EavAttributeName, EavClassName, EavParent } from '@/models/constants/eav';
import { getScreeningUnitIdFromName } from '@/config/app.config';
import { ApplicationDefaultValue } from '@/models/constants/ApplicationDefaultValue';
import { McsisConstants } from '@/models/constants/McsisConstants';
@Component({
    components: {
        InputText,
        InputSelect,
        CheckboxList,
        InformationBlock,
    },
})
export default class AppointmentSpecials extends Vue {
    @Prop() private editMode?: boolean;

    private sexes: CodeDTO[] = [];
    private reasons: CodeDTO[] = [];
    private selected = '';
    
    //#region ----------- Getter, Watcher & Hooks -------------

    private get appointment(): AppointmentDTO {
        return this.$store.getters[GetterTypes.appointment];
    }

    public get checkedReasons(): number[] {
        return this.$store.getters[GetterTypes.reasonLongAppointment];
    }

    public get lngId(): number {
        switch (this.$i18n.locale) {
            case 'en':
                return 2;
            case 'fr':
                return 3;
            case 'it':
                return 4;
            case 'de':
            default:
                return 1;
        }
    }

    public get options(): SelectOptionDTO[] {
        return this.sexes.map((x: CodeDTO) => ({
            value: x.codId.toString(),
            text: this.getTranslation(x),
        } as SelectOptionDTO));
    }

    public get useTRMGenderChoice(): boolean {
        return this.$store.getters[GetterTypes.useTRMGenderChoice];
    }

    public get useLongAppointment(): boolean {
        return this.$store.getters[GetterTypes.useLongAppointment];
    }

    protected async mounted(): Promise<void> {
        this.$store.dispatch(ActionTypes.setDataLoading, true);

        await CodeService.fetchCodes('SEX')
            .then((res: CodeDTO[]) => {
                this.sexes = res;
            });
        await CodeService.fetchCodes('LONG_APPOINTMENT_REASON')
            .then(async (res: CodeDTO[]) => {
              this.reasons = await this.getVisibleReasons(res);
            });

        if (this.appointment.appointmentDetails !== null) {
            this.selected = this.appointment.appointmentDetails.trmGenderId.toString();
        }

        this.$store.dispatch(ActionTypes.setDataLoading, false);
    }

    //#endregion
    //#region ---------------- Events Handlers -------------

    protected onSelectChange(selectedValue: string): void {
        const app = this.appointment;
        this.selected = selectedValue;

        if (app.appointmentDetails !== null) {
            app.appointmentDetails.trmGenderId = Number(selectedValue);
        } else {
            app.appointmentDetails = {
                trmGenderId: Number(selectedValue),
            } as AppointmentDetailsDTO;
        }

        this.$store.dispatch(ActionTypes.setAppointment, app);
    }

    protected checkedReasonsChanged(event: number[]): void {
        const app = this.appointment;

        if (app.appointmentDetails !== null) {
            app.appointmentDetails.longAppointmentReasonsId = event;
            app.appointmentDetails.isLongAppointment = event.length > 0;
            app.appSlotNumber = app.appointmentDetails.isLongAppointment ? 2 : 1;
        } else {
            app.appointmentDetails = {
                longAppointmentReasonsId: event,
                isLongAppointment: event.length > 0,
            } as AppointmentDetailsDTO;
        }

        app.appDate = null;
        app.appEndDate = null;
        app.rooId = 0;

        this.$store.dispatch(ActionTypes.setAppointment, app);
    }

    //#endregion
    //#region ---------------- Helpers -------------

    private getTranslation(code: CodeDTO): string {
        const translation = code.translations.find((x: CodeTranslationDTO) => x.lngId === this.lngId);
        return translation === undefined ? code.codName : translation.caption;
    }

    private getReasonsTranslation(): string {
        let reasonsTranslation = '';
        if (this.checkedReasons.length > 0) {
            if (this.checkedReasons.length === 1) {
                const translation = this.reasons.filter(
                    (x: CodeDTO) => x.codId === this.checkedReasons[0])[0].translations
                    .find((x: CodeTranslationDTO) => x.lngId === this.lngId);
                reasonsTranslation = translation === undefined ? '' : translation.caption;
            } else {
                this.reasons.forEach((x: CodeDTO) => {
                    const translation = x.translations.find((trans: CodeTranslationDTO) => trans.lngId === this.lngId);
                    if (translation !== undefined) {
                        if (reasonsTranslation === '') {
                            reasonsTranslation = translation.caption;
                        } else {
                            reasonsTranslation = reasonsTranslation.concat(', ', translation.caption);
                        }
                    }
                });
            }
        }
        return reasonsTranslation;
    }

    private async getVisibleReasons(reasons: CodeDTO[]): Promise<CodeDTO[]> {
        let visibleReasons: CodeDTO[] = reasons;
        
        const suId = getScreeningUnitIdFromName(this.$route.params.screening);

        if (suId) {
            const hideLongAptReasons = await EavService.getStringEav(
                EavParent.SCREENING_UNIT,
                suId,
                EavClassName.MEETING,
                EavAttributeName.HIDE_LONG_APT_REASON
                );

            if (hideLongAptReasons && hideLongAptReasons.trim() !== ApplicationDefaultValue.String) {
                const codesToHide = hideLongAptReasons
                .split(McsisConstants.CommaSeparator)
                .map(code => code.trim())
                .filter(code => code !== ApplicationDefaultValue.String);

                visibleReasons = visibleReasons.filter(
                    reason => !codesToHide.includes(reason.codName)
                );
            }
        }

        return visibleReasons;
    }
    //#endregion
}
